import { AfterViewInit, Component, ElementRef, Input, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ImagePopupComponent } from '../../widgets/timeline1/image-popup/image-popup.component';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { IOWService } from '../../../../../core/configuration/_services/iow.service';


interface IContent {
  title: string;
  changes: any[];
  description: string;
  attachments: IAttachment[];
  modifiedTime: string;
  author: IAuthor;
  issue : any;
}

interface IAttachment {
  src: string;
}

interface IAuthor {
  admin: boolean;
  _id: string;
  name: string;
}

@Component({
  selector: 'kt-timeline-content',
  templateUrl: './timeline-content.component.html',
  styleUrls: ['./timeline-content.component.scss']
})
export class TimelineContentComponent implements OnInit, AfterViewInit {

  @Input() contents!: IContent[];
  @Input() alternatePlacement = true;
  @Input() emptyStateText = "";
  @Input() moduleType ;
  @Input() iow : Boolean = false;
  private fragment: string | null = null;
  imageExtList = ['png', 'jpg', 'jpeg', 'bmp', 'gif', 'svg', 'webp'];
  content : IContent[];
  hiddenItemCount : Number;
  showMore : Boolean = true;

  constructor(public dialog: MatDialog,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly elementRef: ElementRef,
    private readonly iowService: IOWService,
    private readonly http: HttpClient
  ) { }

  ngAfterViewInit(): void {
    this.waitForImagesToLoad(() => {
      if (this.fragment) {
        this.scrollToFragment(this.fragment);
        this.removeFragmentFromUrl();
        this.fragment = null;
      }
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.hiddenItemCount = this.contents.length - 1;
    this.showMore = true;
    if (this.iow) {
      this.content = this.contents;
      if (this.content.length && this.moduleType === 4 && this.showMore && this.checkCollab()) {
        this.contents = [this.content[0]];
        if (this.content.length > 1) {
          this.contents.push(this.content[1]);
          this.hiddenItemCount = this.content.length - 2;
        }
      }
    }
  }

  ngOnInit(): void {
    this.activatedRoute.fragment.subscribe(fragment => {
      this.fragment = fragment;
    });
    if (this.iow && this.selectedIssue === this.contents[0]?.issue) {
      if (this.contents?.length > 1)
        this.fragment = this.collabId;
    }
    this.contents.forEach(content => {
      if (content.attachments) {
        this.sortAttachments(content.attachments);
      }
    });
  }

  get collabId() {
    return this.iowService.selectedCollabId;
  }

  checkCollab() {
    if (!this.collabId)
      return true;
    else {
      this.showMore = !this.showMore;
      return false;
    }
  }

  onShowMore() {
    this.showMore = !this.showMore;
    if (this.showMore) {
      this.contents = [this.content[0]];
      if(this.content.length > 1) {
        this.contents.push(this.content[1]);
      }
    }
    else {
      this.contents = this.content;
    }
  }

  showMoreCircle() {
    return this.iow && this.hiddenItemCount;
  }

  sortAttachments(attachments: IAttachment[]) {
    attachments.sort((a, b) => {
      const aSupported = this.isSupportedImage(a);
      const bSupported = this.isSupportedImage(b);

      if (aSupported && !bSupported) {
        return 1;
      } else if (!aSupported && bSupported) {
        return -1;
      } else {
        return 0;
      }
    });
  }

  scrollToFragment(fragment: string) {
    const element = document.getElementById(fragment);
    if (element) {
      setTimeout(() => {
        const elementRect = element.getBoundingClientRect();
        const elementPosition = elementRect.top + window.scrollY;
        const offsetPosition = elementPosition - (window.innerHeight / 2) + (element.clientHeight / 2);
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
        this.highlightElement(element);
      }, 100);
    } else {
      console.log(`Element with id '${fragment}' not found`);
    }
  }

  get selectedIssue() {
    return this.iowService.selectedIssueFromQueryParam;
  }

  private highlightElement(element: HTMLElement) {
    const targetDiv = element.querySelector('.content-container');
    if (targetDiv && this.collabId) {
      setTimeout(() => {
        this.iowService.queryTags = [];
        this.iowService.selectedIssueFromQueryParam = '';
        this.iowService.selectedCollabId = '';
      }, 1500);
    }
    if (targetDiv) {
      targetDiv.classList.add('anchor-highlight');
      setTimeout(() => {
        targetDiv.classList.remove('anchor-highlight');
      }, 1500);
    }
  }

  private waitForImagesToLoad(callback: () => void) {
    const images = Array.from(this.elementRef.nativeElement.getElementsByTagName('img'));
    let loadedCount = 0;
    const imageTotal = images.length;
    function imageLoaded() {
      loadedCount++;
      if (loadedCount === images.length) {
        callback();
      }
    }
    images.forEach(img => {
      if (img instanceof HTMLImageElement) {
        if (img.complete) {
          imageLoaded();
        } else {
          img.onload = imageLoaded;
          img.onerror = imageLoaded;
        }
      }
    });

    if (imageTotal === 0) {
      callback();
    }
  }

  private removeFragmentFromUrl() {
    const currentUrlTree = this.router.parseUrl(this.router.url);
    if (currentUrlTree.fragment) {
      const navigationExtras: NavigationExtras = {
        fragment: '',
        queryParamsHandling: 'preserve'
      };
      this.router.navigate([], navigationExtras);
    }
  }

  open = (images, index) => {
    const dialogRef = this.dialog.open(ImagePopupComponent, {
      data: images[index]
    });
  }

  isSupportedImage(item: IAttachment) {
    return !!(this.imageExtList.find(ext => item.src.includes(ext)));
  }

  getFileName(item: IAttachment) {
    let url = item.src;
    const array = url.split('/');
    let name = array[array.length-1];
    return name.split('?')[0];
  }

  downloadFile(item: IAttachment) {
    this.http.get(item.src, { responseType: 'blob' }).subscribe(blob => {
      const a = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = this.getFileName(item);
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(objectUrl);
      document.body.removeChild(a);
    });
  }
}
